import { defineStore } from 'pinia'
import axios from 'axios'
import type { LoanThankYouPage, LongTermForm, ProviderForForms, Route, SignalResponse } from 'loans/types/forms'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { stripTrailingSlash } from 'loans/services/utils'

export const useFormStore = defineStore('form-store', {
  state: () => ({
    content: {} as LongTermForm,
    contentThankYou: {} as LoanThankYouPage,
    contentXsell: {} as any,
    provider: {} as ProviderForForms,
    providerName: '',
    values: {
      loanAmount: 150000,
      repayment: 3000,
      loanAmountMax: 1200000,
      loanAmountMin: 1000,
      loanTermMax: 120,
      loanTermMin: 1,
      loanAmountSliderRange: [
        '30%,20000,5000',
        '54%,100000,10000',
        '84%,300000,50000',
      ],
      nationality: 'CZ',
      portal: `www.${useRuntimeConfig().public.storyblokPortal.replace('-', '.')}`,
    } as any, // type it later
    xIdentifier: '',
    baseUrl: stripTrailingSlash(useRuntimeConfig().public.loanApi),
    redirectUrl: '',
    route: {} as Route,
    loading: false,
    isL20: false,
    loanOfferPath: useRuntimeConfig().public.loanOfferPath,
  }),
  getters: {
    calculatorSB(state) {
      const calculatorSB = state.content.calculator[0]
      calculatorSB.image = state.provider.content.logo || null
      calculatorSB.loanAmountMax = Number(state.provider.content.loanAmountMax)
      calculatorSB.loanAmountMin = Number(state.provider.content.loanAmountMin)
      calculatorSB.loanTermMax = Number(state.provider.content.loanTermMax)
      calculatorSB.loanTermMin = Number(state.provider.content.loanTermMin)
      const interest = state.provider.content.interest.replace(',', '.')
      calculatorSB.interest = Number(interest)
      calculatorSB.amountSliderRange = state.provider.content.loanAmountSliderRange || state.values.loanAmountSliderRange
      calculatorSB.payDayLoan = state.provider.content.payDayLoan || false
      calculatorSB.unitsPlural = calculatorSB.payDayLoan ? state.content.payDayUnitsPlural?.split(',') : (`${this.content.longTermUnitsPlural!},${this.content.longTermYearsPlural!}`).split(',')
      return calculatorSB
    },
  },
  actions: {
    async getContent() {
      const data: LongTermForm = await useStoryblokApiV2().getForm('/pujcky-a-uvery/zadost')
      data.fields?.forEach(field => Object.assign(field, { defaultError: field.defaultError || `${field.label} ${data.defaultFieldError}` }))
      this.content = data
      if (!(this.providerName in this.content.components) || !this.content.components[this.providerName].content.codeRef)
        throw createError({ statusCode: 404 })
      this.provider = this.content.components[this.providerName]
      this.values.loanAmount = Number(this.provider.content.loanAmountDefault)
      this.values.repayment = Number(this.provider.content.loanRepaymentDefault)
      this.values.payDayLoan = this.provider.content.payDayLoan
      this.values.loanTermType = this.provider.content.payDayLoan ? 'Days' : 'Months'
      this.xIdentifier = this.content.components[this.providerName].content.codeRef
    },
    async getContentThankYou() {
      const data = await useStoryblokApiV2().getForm(`/pujcky-a-uvery/${this.route.params.thank}`, { resolve_relations: ['loanCrossSell.components', 'PageLoanXsellProvider.provider'] })
      this.contentThankYou = data
      this.provider = this.content.components[this.providerName]
      await this.webSocket(true)
      if (data.crossSell?.length) data.crossSell = { ...data.crossSell[0] }
      if (data.crossSell.components?.length)
        data.crossSell.components = useKeyBy(data.crossSell.components, item => useCamelCase(item.slug))
      this.contentXsell = data.crossSell
    },
    async getContentL20() {
      const data: LongTermForm = await useStoryblokApiV2().getForm('/pujcky-a-uvery/pujcka-na-miru/srovnani')
      data.fields?.forEach(field => Object.assign(field, { defaultError: field.defaultError || `${field.label} ${data.defaultFieldError}` }))
      this.content = data
      this.xIdentifier = '2-0'
      this.isL20 = true
      this.provider = {
        content:
        {
          loanAmountMax: this.values.loanAmountMax,
          loanAmountMin: this.values.loanAmountMin,
          loanTermMax: this.values.loanTermMax,
          loanTermMin: this.values.loanTermMin,
          interest: this.content.calculator[0].interest.toString(),
        },
      } as ProviderForForms
    },
    async getDemand(thank = false) {
      const { data, status } = await useGetDemand({ id: this.values.id })
      if (status === 200) {
        Object.assign(this.values, data)
        if (this.values.phoneNumber) this.values.phoneNumber = this.values.phoneNumber.replace(/\+420/g, '')
        if (this.values.bankAccount) this.values.bankAccount = this.values.bankAccountPrefix ? `${this.values.bankAccountPrefix}-${this.values.bankAccountNumber}` : this.values.bankAccountNumber
        if (this.values.streetName && this.values.streetNumber) this.values.street = `${this.values.streetName} ${this.values.streetNumber}`
        if (this.values.firstName && this.values.lastName) this.values.fullName = this.values.middleName ? `${this.values.firstName} ${this.values.middleName} ${this.values.lastName}` : `${this.values.firstName} ${this.values.lastName}`
        if (!thank) this.values.repayment = Math.round((this.values.loanAmount / this.values.loanTerm) + ((this.values.loanAmount / this.values.loanTerm) * (+this.calculatorSB.interest / 100)))
        this.values.sanctionTerms = true
      }
    },
    async createDemand(phoneNumber = true) {
      this.loading = true
      if (phoneNumber) this.values.phoneNumber = `+420${this.values.phoneNumber}`
      const { data, status, statusText } = await useCreateDemand({ body: JSON.stringify(this.values), headers: { 'X-Product-Identifier': `products/loans/${this.xIdentifier}` } })
      if (status === 200) {
        this.values.id = data.id
        const params = useUrlSearchParams('history')
        params.id = this.values.id
      }
      else if (status === 400 && statusText) {
        this.callThrowError(statusText)
      }
      this.loading = false
    },
    async updateDemand(end = false, phoneNumber = true) {
      this.loading = true
      if (phoneNumber || this.values.phoneNumber) this.values.phoneNumber = this.values.phoneNumber.includes('+420') ? this.values.phoneNumber : `+420${this.values.phoneNumber}`
      this.values.formCompleted = end
      if (!end) {
        const { data, status, statusText } = await useUpdateDemand({ body: JSON.stringify(this.values), id: this.values.id })
        if (status === 400 && statusText)
          this.callThrowError(statusText)

        this.values.id = data.id
        const params = useUrlSearchParams('history')
        params.id = this.values.id
        this.loading = false
      }
      else {
        const { status } = await usePrecheckPost({ data: JSON.stringify(this.values), headers: { 'X-Product-Identifier': `products/loans/${this.xIdentifier}` } })
        if (status === 200) {
          if (this.isL20) {
            await navigateTo(`${this.loanOfferPath}/pujcka-na-miru/nabidky?id=${encodeURIComponent(this.values.id)}`, {
              external: true,
            })
          }
          else { this.webSocket() }
        }
      }
    },
    callThrowError(data: any) {
      const error = { response: { data: { errors: data } } }
      error.response.data.errors.forEach((insideError: any, index: number) => {
        const newKey = useCamelCase(insideError.propertyName)
        Object.assign(error.response.data.errors, { [newKey]: '' })
        delete error.response.data.errors[index]
      })
      Object.assign(error.response, { _data: { errors: error.response.data.errors } })
      this.loading = false
      throw error
    },
    async webSocket(thank?: boolean) {
      try {
        const socket = new HubConnectionBuilder().configureLogging(LogLevel.Information).withUrl(`${this.baseUrl}/signalr/`).withAutomaticReconnect().build()
        socket.on('PrecheckResult', (_data: SignalResponse) => { })
        socket.on('PrecheckFinished', (data: SignalResponse[]) => {
          if (data?.length && (data[0].status === 'PrecheckSuccess' || data[0].status === 'ApplicationSuccess') && !thank) {
            axios.post(`${this.baseUrl}/application`, { partner: this.xIdentifier, id: this.values.id })
          }
          else if (data?.length && data[0].finalizingActionUrl && thank) {
            this.redirectUrl = data[0].finalizingActionUrl
            socket.stop()
          }
          else if (!thank) {
            navigateTo({ path: `/pujcky-a-uvery/${this.route.params.provider}/zamitnuto-partner`, query: { id: this.values.id } })
          }
        })
        socket.on('ApplicationResult', (data: SignalResponse) => {
          if (this.route.params.thank === 'dekujeme' || this.route.params.thank === 'zamitnuto' || this.route.params.thank === 'zamitnuto-partner') return
          if (data.status === 'ApplicationSuccess')
            navigateTo({ path: `/pujcky-a-uvery/${this.route.params.provider}/dekujeme`, query: { id: this.values.id } })
          else
            navigateTo({ path: `/pujcky-a-uvery/${this.route.params.provider}/zamitnuto-partner`, query: { id: this.values.id } })

          socket.stop()
        })
        await socket.start()
        await socket.invoke('SubscribeForResults', this.values.id)
      }
      catch (err) {
        // state.value = 'error'
      }
    },
  },
})
